// Default data objects for a fresh project (theme, content, page styles).
// Pure literals extracted from editor.store; types are import-only (no runtime cycle).
import type { ElementStyles, StyleProperties } from '@webbuilder/style-engine'
import type { PageTheme, ProjectContent } from './editor.store'

export const DEFAULT_PROJECT_CONTENT: ProjectContent = {
  business: {
    name: 'Mi negocio',
    tagline: 'Soluciones profesionales',
    description: 'Describe aquí lo que hace especial a este negocio.',
    logo: '',
  },
  navigation: {
    items: [
      { id: 'nav-home', label: 'Inicio', url: '/' },
      { id: 'nav-services', label: 'Servicios', url: '/servicios' },
      { id: 'nav-contact', label: 'Contacto', url: '/contacto' },
    ],
  },
  collections: [
    {
      id: 'services',
      name: 'Servicios',
      icon: 'star',
      color: '#F59E0B',
      fields: [
        { id: 'title', name: 'Título', type: 'title' },
        { id: 'status', name: 'Estado', type: 'text' },
        { id: 'created', name: 'Fecha de creación', type: 'text' },
        { id: 'categories', name: 'Categorías', type: 'text' },
        { id: 'description', name: 'Descripción', type: 'richText' },
        { id: 'content', name: 'Contenido', type: 'richText' },
        { id: 'image', name: 'Imagen', type: 'image' },
      ],
      items: [
        {
          id: 'item-1',
          values: {
            title: 'Resumen',
            status: 'Público',
            created: '2026-06-08',
            categories: 'General',
            description: 'Una descripción general para presentar este contenido.',
            content: '<p>Este contenido resume la información principal de esta colección.</p>',
            image: 'https://images.unsplash.com/photo-1497366754035-f200968a6e72?w=1200&h=800&fit=crop&auto=format',
          },
        },
        {
          id: 'item-2',
          values: {
            title: 'Características',
            status: 'Público',
            created: '2026-06-07',
            categories: 'General',
            description: 'Puntos clave y detalles importantes.',
            content: '<p>Usa esta entrada para explicar características, detalles o información relevante.</p>',
            image: 'https://images.unsplash.com/photo-1497215728101-856f4ea42174?w=1200&h=800&fit=crop&auto=format',
          },
        },
        {
          id: 'item-3',
          values: {
            title: 'Beneficios',
            status: 'Público',
            created: '2026-06-06',
            categories: 'General',
            description: 'Ventajas principales para el usuario.',
            content: '<p>Describe aquí los beneficios principales de este contenido o servicio.</p>',
            image: 'https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?w=1200&h=800&fit=crop&auto=format',
          },
        },
        {
          id: 'item-4',
          values: {
            title: 'Proceso',
            status: 'Público',
            created: '2026-06-05',
            categories: 'General',
            description: 'Una explicación simple del flujo o método.',
            content: '<p>Explica el proceso, pasos o metodología de forma clara y breve.</p>',
            image: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=1200&h=800&fit=crop&auto=format',
          },
        },
        {
          id: 'item-5',
          values: {
            title: 'Contacto',
            status: 'Público',
            created: '2026-06-04',
            categories: 'General',
            description: 'Información final o llamada a la acción.',
            content: '<p>Agrega una conclusión, llamada a la acción o información de contacto.</p>',
            image: 'https://images.unsplash.com/photo-1500534314209-a25ddb2bd429?w=1200&h=800&fit=crop&auto=format',
          },
        },
      ],
    },
  ],
  contact: {
    email: '',
    phone: '',
    address: '',
  },
}

export const DEFAULT_PAGE_THEME: PageTheme = {
  presetId: 'saas-2027',
  typography: {
    fontPairId: 'saas-2027',
    headingFont: "'Inter', sans-serif",
    bodyFont: "'Inter', sans-serif",
  },
  colors: {
    background: '#FAFBF7',
    backgroundSecondary: '#F1F3E9',
    card: '#FAFBF7',
    surface: '#E1E8D0',
    border: '#B2B9A5',
    text: '#0B0D0A',
    muted: '#58692C',
    primary: '#0B0D0A',
    accent: '#D7FF64',
  },
  roles: {
    section: {
      background: 'var(--wb-background)',
      color: 'var(--wb-text)',
    },
    heading1: {
      color: 'var(--wb-text)',
      fontFamily: "'Inter', sans-serif",
      fontSize: '64px',
      fontWeight: '300',
      lineHeight: '1.02',
      letterSpacing: '-0.5px',
    },
    heading2: {
      color: 'var(--wb-text)',
      fontFamily: "'Inter', sans-serif",
      fontSize: '2.8rem',
      fontWeight: '300',
      lineHeight: '1.08',
      letterSpacing: '-0.5px',
    },
    heading3: {
      color: 'var(--wb-text)',
      fontFamily: "'Inter', sans-serif",
      fontSize: '1.55rem',
      fontWeight: '300',
      lineHeight: '1.2',
      letterSpacing: '0',
    },
    heading4: {
      color: 'var(--wb-text)',
      fontFamily: "'Inter', sans-serif",
      fontSize: '1.15rem',
      fontWeight: '500',
      lineHeight: '1.3',
      letterSpacing: '0',
    },
    heading5: {
      color: 'var(--wb-text)',
      fontFamily: "'Inter', sans-serif",
      fontSize: '0.95rem',
      fontWeight: '500',
      lineHeight: '1.35',
      letterSpacing: '-0.5px',
    },
    heading6: {
      color: 'var(--wb-text)',
      fontFamily: "'Inter', sans-serif",
      fontSize: '0.78rem',
      fontWeight: '500',
      lineHeight: '1.35',
      letterSpacing: '0.04em',
    },
    paragraph: {
      color: 'var(--wb-text)',
      fontFamily: "'Inter', sans-serif",
      fontSize: '16px',
      fontWeight: '400',
      lineHeight: '1.5',
      letterSpacing: '0',
    },
    smallText: {
      color: 'var(--wb-text)',
      opacity: '0.6',
      fontFamily: "'Inter', sans-serif",
      fontSize: '13px',
      fontWeight: '400',
      lineHeight: '1.5',
      letterSpacing: '0',
      textTransform: 'none',
    },
    eyebrow: {
      color: 'var(--wb-text)',
      opacity: '0.6',
      fontFamily: "'Inter', sans-serif",
      fontSize: '11px',
      fontWeight: '400',
      lineHeight: '1.25',
      letterSpacing: '0.12em',
      textTransform: 'uppercase',
      display: 'block',
      padding: '0',
      borderWidth: '0',
      borderStyle: 'none',
      borderColor: 'transparent',
      background: 'transparent',
    } as StyleProperties,
    buttonPrimary: {
      background: 'var(--wb-muted)',
      color: 'var(--wb-background)',
      fontFamily: "'Inter', sans-serif",
      fontSize: '14px',
      fontWeight: '400',
      letterSpacing: '0',
      borderRadius: '24px',
      padding: '12px 24px',
      width: 'fit-content',
      minHeight: '46px',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderWidth: '0px',
      borderStyle: 'solid',
      borderColor: 'var(--wb-muted)',
      cursor: 'pointer',
      boxShadow: '0 8px 24px rgba(15,23,42,0.00)',
      textDecoration: 'none',
    },
    buttonSecondary: {
      background: 'transparent',
      color: 'var(--wb-muted)',
      fontFamily: "'Inter', sans-serif",
      fontSize: '14px',
      fontWeight: '400',
      letterSpacing: '0',
      borderRadius: '24px',
      padding: '12px 22px',
      width: 'fit-content',
      minHeight: '46px',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderWidth: '1.5px',
      borderStyle: 'solid',
      borderColor: 'var(--wb-muted)',
      cursor: 'pointer',
      textDecoration: 'none',
    },
    card: {
      background: 'var(--wb-card)',
      backgroundColor: 'var(--wb-card)',
      color: 'var(--wb-text)',
      borderRadius: '24px',
      borderColor: 'var(--wb-border)',
      borderWidth: '1px',
      borderStyle: 'solid',
      boxShadow: 'none',
      padding: '42px',
      paddingTop: '42px',
      paddingRight: '42px',
      paddingBottom: '42px',
      paddingLeft: '42px',
    },
    image: {
      borderRadius: '8px',
      objectFit: 'cover',
      objectPosition: 'center',
      overflow: 'hidden',
      boxShadow: 'none',
    },
    /* Un icono de fábrica: 20 px de símbolo y 8 de aire —una caja de 36—. Eran
       24 y 10 (44 px), y en una sección con varios iconos, o al seleccionar uno,
       pesaban más que el texto que acompañan. */
    icon: {
      color: 'var(--wb-primary)',
      background: 'var(--wb-backgroundSecondary)',
      borderRadius: '24px',
      padding: '8px',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    badge: {
      color: 'var(--wb-text)',
      fontFamily: "'Inter', sans-serif",
      fontSize: '11px',
      fontWeight: '650',
      lineHeight: '1.2',
      letterSpacing: '0.02em',
      textTransform: 'none',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 'fit-content',
      padding: '3px 8px',
      borderRadius: '8px',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'rgba(15,23,42,0.14)',
      background: 'var(--wb-surface)',
    } as StyleProperties,
    rating: {
      color: 'var(--wb-accent)',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '3px',
    },
    avatarGroup: {
      display: 'flex',
      alignItems: 'center',
      gap: '0px',
    },
    stat: {
      background: 'var(--wb-card)',
      color: 'var(--wb-text)',
      borderRadius: '8px',
      borderColor: 'var(--wb-border)',
      borderWidth: '1px',
      borderStyle: 'solid',
      boxShadow: '0 4px 16px rgba(15,23,42,0.06)',
      padding: '20px',
      fontFamily: "'Inter', sans-serif",
      fontSize: '1.55rem',
      fontWeight: '850',
      lineHeight: '1.2',
    },
    link: {
      background: 'transparent',
      color: 'var(--wb-primary)',
      fontFamily: "'Inter', sans-serif",
      fontSize: '14px',
      fontWeight: '400',
      letterSpacing: '0',
      borderRadius: '24px',
      padding: '10px 0',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      textDecoration: 'none',
      /* Como los otros dos roles de botón. La regla base ya lo garantiza en la
         página publicada; esto lo deja también dentro del tema, para que el rol
         no mienta sobre lo que es: algo que se pulsa. */
      cursor: 'pointer',
    },
    navigation: {
      background: 'var(--wb-accent)',
      color: 'var(--wb-text)',
      borderColor: 'var(--wb-border)',
      borderWidth: '1px',
      borderStyle: 'solid',
      paddingTop: '16px',
      paddingBottom: '16px',
      paddingLeft: '24px',
      paddingRight: '24px',
      boxShadow: '0 4px 16px rgba(15,23,42,0.04)',
    },
    footer: {
      background: 'var(--wb-text)',
      color: 'var(--wb-background)',
      paddingTop: '64px',
      paddingBottom: '64px',
      paddingLeft: '24px',
      paddingRight: '24px',
    },
  },
  roleProps: {
    icon: { iconSize: 20 },
    badge: { iconSize: 14 },
    rating: { ratingValue: 5, ratingSize: 16 },
    avatarGroup: { avatarCount: 4, avatarSize: 36 },
  },
  // Responsive type scale. Desktop sizes live in `roles`; these only shrink the
  // larger headings on smaller screens (best-practice fluid scale). Body/párrafo and
  // small stay put — body never drops below 16px to avoid iOS auto-zoom.
  roleBreakpoints: {
    tablet: {
      heading1: { fontSize: '44px' },
      heading2: { fontSize: '36px' },
      heading3: { fontSize: '22px' },
    },
    mobile: {
      heading1: { fontSize: '34px', lineHeight: '1.08' },
      heading2: { fontSize: '28px', lineHeight: '1.12' },
      heading3: { fontSize: '20px' },
      heading4: { fontSize: '17px' },
    },
  },
}

export const DEFAULT_PAGE_STYLES: ElementStyles = {
  desktop: {
    overflowX: 'hidden',
    overflowY: 'visible',
  },
}
